import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import type { AnswerWidgets } from '@openx/components-core/lib/AiChat/answerWidgets';
import { useCallback, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { answerDividerSx } from '../../AnswerWidgets/utils/styles';
import { detailsToggleSx } from '../styles';
import { splitAnswerAtFirstWidget } from '../utils/splitAnswerAtFirstWidget';
import { OpenXAgentMarkdown } from './OpenXAgentMarkdown';

type ChatMessageAnswerProps = {
  content: string;
  widgets?: AnswerWidgets | undefined;
};

// agreed with the agent team for now: everything from the first widget in the answer goes behind 'Show details'
export const ChatMessageAnswer = ({ content, widgets }: ChatMessageAnswerProps) => {
  const { t } = useTranslation();
  const detailsId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const { visible, hiddenDetails } = useMemo(() => splitAnswerAtFirstWidget(content), [content]);
  const toggleDetails = useCallback(() => setIsExpanded(expanded => !expanded), []);

  if (hiddenDetails === null) {
    return <OpenXAgentMarkdown content={content} widgets={widgets} />;
  }

  return (
    <>
      {visible === '' ? null : <OpenXAgentMarkdown content={visible} widgets={widgets} />}
      <ButtonBase
        sx={detailsToggleSx}
        aria-expanded={isExpanded}
        aria-controls={detailsId}
        onClick={toggleDetails}
        data-test="message-details-toggle"
      >
        {isExpanded ? t('Hide details') : t('Show details')}
        {isExpanded ? <ExpandLess /> : <ExpandMore />}
      </ButtonBase>
      <Box id={detailsId} hidden={!isExpanded} data-test="message-details">
        {isExpanded ? (
          <>
            <Divider sx={answerDividerSx} />
            <OpenXAgentMarkdown content={hiddenDetails} widgets={widgets} />
          </>
        ) : null}
      </Box>
    </>
  );
};
